import { Link } from 'react-router-dom';
import { ArrowRight, Stethoscope, Users, Check, Calendar, Heart, Sparkles, FileText, BarChart } from '../components/Icons.jsx';

export default function WorkExperience() {
  return (
    <>
      <div className="page-head">
        <div className="kicker" style={{ color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, fontSize: 12 }}>Medicine Summer Career Camp</div>
        <h1>Real NHS placements. Hospital + hospice. Surgeon-led.</h1>
        <p className="subtitle">London runs the full hospital + hospice week — rotations across A&amp;E, surgery, GP and a hospice setting, led by doctors, surgeons and medical researchers. Birmingham and Manchester run GP-shadowing cohorts. Bursaries cover 50%, 75% or 100% for widening-participation applicants.</p>
      </div>

      <section className="card" style={{
        background: 'linear-gradient(135deg, var(--feature-from), var(--feature-via), var(--feature-to))',
        color: '#fff', border: 'none', marginBottom: 24, padding: '28px 32px'
      }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ width: 56, height: 56, background: 'rgba(255,255,255,0.15)', borderRadius: 14, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <Stethoscope width="28" height="28" />
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 24, fontWeight: 700, letterSpacing: '-0.01em' }}>What makes this different.</h2>
            <p style={{ margin: 0, color: '#e9d5ff', maxWidth: 620 }}>Most "work experience" is a recorded video. Career Camp is a real cohort, a real hospital, a real hospice — with a reflection workbook mapped to GMC outcomes so it actually lands in your personal statement.</p>
          </div>
        </div>
      </section>

      <div className="parents-grid">
        <CityCard
          city="London"
          tag="In-person · 5 days"
          bullets={[
            'Hospital rotations + hospice placement',
            'Surgery, A&E, GP, mental health observations',
            'Workshops with active NHS clinicians',
            'Visits to research institutions'
          ]}
        />
        <CityCard
          city="Manchester"
          tag="In-person · 5 days"
          bullets={[
            'GP surgery placement (real patient contact)',
            'Hospital rotations across specialties',
            'Same Career Camp programme, Manchester cohort',
            'Strong route for the north-west'
          ]}
        />
        <CityCard
          city="Birmingham"
          tag="In-person · 5 days"
          bullets={[
            'Hospital visits + clinician-led workshops',
            'Case-based discussions on Midlands healthcare',
            'Research-track sessions',
            'Best mid-country option'
          ]}
        />
      </div>

      <div className="card" style={{ marginTop: 24, marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 22 }}>Bursaries — 50%, 75% or 100%</h2>
        <p style={{ margin: 0, color: 'var(--text-2)' }}>If you're widening-participation eligible (free-school-meals, first-in-family, bursary-funded school, or a low-income household), Medic Mind covers half, three-quarters or the full cost of Career Camp. This is the lever that closes the gap with privately-tutored applicants. Ask in your free 15-min call.</p>
      </div>

      <h2 style={{ margin: '0 0 14px', fontSize: 22 }}>Free work experience tools</h2>
      <div className="parents-grid" style={{ marginBottom: 24 }}>
        <ToolCard
          to="/we-quiz"
          icon={<BarChart />}
          tag="Diagnostic"
          title="How strong is your work experience?"
          body="8 questions. Scores you on clinical exposure, breadth, reflection depth and longitudinal commitment — and tells you what to add next."
        />
        <ToolCard
          to="/we-finder"
          icon={<Stethoscope />}
          tag="Matcher + tracker"
          title="Find work experience & log hours"
          body="Filter by region, format and goal. See what's available — Career Camp first when it fits, longitudinal alternatives stacked underneath. Browser-side logbook included."
        />
        <ToolCard
          to="/we-reflection"
          icon={<FileText />}
          tag="Writer"
          title="Reflection generator"
          body="Turn one placement memory into a PS-ready paragraph using What / So what / Now what — mapped to a GMC outcome admissions tutors actually score against."
        />
      </div>

      <section className="parents-timeline" style={{ marginTop: 12 }}>
        <h2>When to do it</h2>
        <div className="timeline">
          <Row when="Year 11–12" what="Start the longitudinal piece early" body="A care home volunteer slot or hospice befriender role costs nothing and compounds across 12+ months — admissions tutors love the longitudinal arc." />
          <Row when="Year 12 summer" what="Best window for Career Camp" body="UCAT prep hasn't peaked yet. Five days of real clinical exposure here pays back across UCAT (especially SJT), personal statement and interview." />
          <Row when="Year 13 anytime" what="Reflect on what you have" body="If schedule is impossible, use the reflection generator to extract maximum value from existing placements. A well-reflected one-day shadow beats an un-reflected week." />
        </div>
      </section>

      <section className="parents-cta" style={{ marginTop: 28 }}>
        <div>
          <h2>See available Career Camp cohorts</h2>
          <p>London, Birmingham &amp; Manchester run year-round. Cohorts are capped — early enquiries get first pick of dates.</p>
        </div>
        <a className="btn" href="https://www.medicmind.co.uk/contact-us/" target="_blank" rel="noreferrer">Book free call <ArrowRight width="16" height="16" /></a>
      </section>
    </>
  );
}

function CityCard({ city, tag, bullets }) {
  return (
    <div className="parents-card">
      <div className="icon-wrap"><Stethoscope /></div>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>{tag}</div>
      <h3>{city}</h3>
      <ul style={{ listStyle: 'none', padding: 0, margin: '10px 0 0', display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--text-2)', fontSize: 14 }}>
        {bullets.map((b) => (
          <li key={b} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ color: 'var(--accent)' }}>▸</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ToolCard({ to, icon, tag, title, body }) {
  return (
    <Link to={to} className="parents-card" style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}>
      <div className="icon-wrap">{icon}</div>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent)', fontWeight: 700, marginBottom: 4 }}>{tag}</div>
      <h3>{title}</h3>
      <p style={{ color: 'var(--text-2)', fontSize: 14, margin: '8px 0 12px' }}>{body}</p>
      <span style={{ color: 'var(--accent)', fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6 }}>Open tool <ArrowRight width="14" height="14" /></span>
    </Link>
  );
}

function Row({ when, what, body }) {
  return (
    <div className="timeline-row">
      <div className="when">{when}</div>
      <div className="what">
        <h4>{what}</h4>
        <p>{body}</p>
      </div>
    </div>
  );
}
